import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataMatrixScanner } from '@/lib/qr/DataMatrixScanner';
import { PageLayout } from '@/components/shared/PageLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Camera, Cpu, Wrench, Plus } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card } from '@/components/ui/card/Card';
import { Button } from '@/components/ui/button';
import { KeyenceScannerCard } from '../components/KeyenceScannerCard';
import { CameraScannerCard } from '../components/CameraScannerCard';
import { ManualIdInput } from '../components/ManualIdInput';
import { AddToolModal } from '../components/AddToolModal';
import { useToolSummaries } from '../hooks/useToolSummaries';

export function TraceabilityScan() {
  const navigate = useNavigate();
  const [detectedId, setDetectedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('keyence');
  const [isAddToolOpen, setIsAddToolOpen] = useState(false);
  const [prefillToolId, setPrefillToolId] = useState<string | undefined>(undefined);
  // ID handed to the creation modal by "Créer cet outil" (empty from the plain "add" button)
  const [createToolId, setCreateToolId] = useState('');
  const { summaries, refresh: refreshSummaries } = useToolSummaries();
  const scanner = useMemo(() => new DataMatrixScanner(), []);
  const { t } = useTranslation();

  useEffect(() => {
    if (!detectedId) return;
    const timer = window.setTimeout(() => {
      navigate(`/traceability/form?toolId=${encodeURIComponent(detectedId)}`);
    }, 500);
    return () => window.clearTimeout(timer);
  }, [detectedId, navigate]);

  const handleToolDetected = useCallback((toolId: string) => {
    setDetectedId(toolId);
  }, []);

  const handleManualSubmit = useCallback(
    (toolId: string) => {
      navigate(`/traceability/form?toolId=${encodeURIComponent(toolId)}`);
    },
    [navigate]
  );

  const handleToolCreated = useCallback(
    (toolId: string) => {
      setIsAddToolOpen(false);
      setPrefillToolId(toolId);
      refreshSummaries();
    },
    [refreshSummaries]
  );

  const openAddTool = useCallback((toolId: string) => {
    setCreateToolId(toolId);
    setIsAddToolOpen(true);
  }, []);

  const handleDevBypass = useCallback(() => {
    navigate(`/traceability/form?toolId=${encodeURIComponent('Test EGO2')}`);
  }, [navigate]);

  const breadcrumbItems = [
    {
      label: t('common.brand'),
      onClick: () => navigate('/'),
    },
    {
      label: t('traceability.scanPage.hero.breadcrumb'),
    },
  ];

  // Shown next to both scanners; each tab renders its own instance.
  const scanAside = (
    <aside className="flex flex-col gap-4">
      <ManualIdInput
        onSubmit={handleManualSubmit}
        defaultValue={prefillToolId}
        summaries={summaries}
        onCreate={openAddTool}
      />

      <Button variant="outline" className="w-full" onClick={() => openAddTool('')}>
        <Plus aria-hidden />
        {t('traceability.scanPage.addTool.button')}
      </Button>

      {import.meta.env.DEV && (
        <Card as="section" tier="muted" className="flex flex-col gap-3 text-sm">
          <div className="flex items-center gap-2 font-semibold text-foreground">
            <Wrench className="h-4 w-4 text-primary" />
            {t('traceability.scanPage.dev.title')}
          </div>
          <p className="text-muted-foreground">{t('traceability.scanPage.dev.description')}</p>
          <Button className="w-full" onClick={handleDevBypass}>
            <Wrench aria-hidden />
            {t('traceability.scanPage.dev.button')}
          </Button>
        </Card>
      )}
    </aside>
  );

  return (
    <PageLayout contentClassName="max-w-6xl">
      <div className="flex flex-col gap-6">
        <PageHeader
          breadcrumb={breadcrumbItems}
          title={t('traceability.scanPage.hero.title')}
          description={t('traceability.scanPage.hero.description')}
        />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="camera" className="gap-2">
              <Camera className="h-4 w-4" />
              {t('traceability.scanPage.tabs.camera')}
            </TabsTrigger>
            <TabsTrigger value="keyence" className="gap-2">
              <Cpu className="h-4 w-4" />
              {t('traceability.scanPage.tabs.keyence')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="camera" className="mt-0">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
              <CameraScannerCard
                scanner={scanner}
                detectedId={detectedId}
                onDetected={handleToolDetected}
                isActive={activeTab === 'camera'}
              />

              {scanAside}
            </div>
          </TabsContent>

          <TabsContent value="keyence" className="mt-0">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
              <KeyenceScannerCard onDetected={handleToolDetected} />

              {scanAside}
            </div>
          </TabsContent>
        </Tabs>

        <AddToolModal
          isOpen={isAddToolOpen}
          onClose={() => setIsAddToolOpen(false)}
          onSuccess={handleToolCreated}
          initialToolId={createToolId}
        />
      </div>
    </PageLayout>
  );
}
